import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Alert, CircularProgress,
  IconButton, Card, CardContent, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Checkbox, Avatar,
  LinearProgress, InputAdornment, Tabs, Tab, TablePagination,
} from '@mui/material';
import {
  ArrowBack as BackIcon, CompareArrows as ReconcileIcon,
  Warning as WarningIcon, CheckCircle as CheckIcon,
  Add as AddIcon, ShoppingCart as CartIcon,
  Search as SearchIcon, ErrorOutline as IssueIcon,
  BrokenImage as BrokenImageIcon, AttachMoney as PriceIcon,
} from '@mui/icons-material';
import authInstance from '../../services/httpClient';
import { useScrollToListTop } from '../../hooks/useScrollToListTop';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UberOrphan {
  sku: string;
  name: string;
  id: string;
  imageUrl: string | null;
  priceCents: number;
}

interface CatalogMissing {
  ID: string;
  SKU: string;
  Name: string;
  Category: string;
  PriceTier1: number;
  PriceTier3: number;
  PriceTiers?: Record<string, number>;
  ShortDescription: string;
  Description: string;
  Attachments?: Array<{ IsDefault: boolean; DownloadUrl: string }>;
  AdditionalAttribute4?: string;
}

interface ProductIssue {
  sku: string;
  name: string;
  uberId: string;
  issue: 'price_mismatch' | 'missing_image' | 'price_mismatch_and_missing_image';
  uberPriceCents: number;
  expectedPriceCents: number;
  dbPriceTier1: number;
  imageUrl: string | null;
  category: string;
}

interface UberCategory {
  id: string;
  title: string;
  subtitle?: string;
  itemCount: number;
  isActive: boolean;
}

interface ReconcileResult {
  onUberNotInCatalog: UberOrphan[];
  inCatalogNotOnUber: CatalogMissing[];
  issues: ProductIssue[];
  summary: {
    uberItemCount: number;
    catalogProductCount: number;
    onUberNotInCatalog: number;
    inCatalogNotOnUber: number;
    priceMismatches: number;
    missingImages: number;
    totalIssues: number;
    markupDivisor: number;
  };
}

interface Props {
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PURPLE = '#7b1fa2';
const PURPLE_DARK = '#6a1b9a';
const PURPLE_LIGHT = '#f3e5f5';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);

/** Tier 1 price price */
const getProductPrice = (p: CatalogMissing): number =>
  p.PriceTier1 ?? p.PriceTier3 ?? 0;

/** Uber price = Tier1 / divisor */
const calcUberPrice = (tier1: number, divisor = 0.8275): number =>
  Math.round((tier1 / divisor) * 100) / 100;

const getProductImage = (p: CatalogMissing): string =>
  p.Attachments?.find((a) => a.IsDefault)?.DownloadUrl ||
  p.Attachments?.[0]?.DownloadUrl ||
  '';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const UberMenuReconciliation: React.FC<Props> = ({ onBack }) => {
  const [storeId, setStoreId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<ReconcileResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Tab state
  const [tab, setTab] = useState(0);

  // Missing items selection + batch add
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [batchAdding, setBatchAdding] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ added: number; skipped: number; total: number } | null>(null);
  const [confirmBatch, setConfirmBatch] = useState(false);

  // Categories for batch add
  const [categories, setCategories] = useState<UberCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState('');

  // Filters
  const [orphanFilter, setOrphanFilter] = useState('');
  const [missingFilter, setMissingFilter] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [issueTypeFilter, setIssueTypeFilter] = useState<'all' | 'price_mismatch' | 'missing_image'>('all');

  // Pagination
  const [orphanPage, setOrphanPage] = useState(0);
  const orphanTopRef = useScrollToListTop(orphanPage);
  const [orphanRowsPerPage, setOrphanRowsPerPage] = useState(50);
  const [missingPage, setMissingPage] = useState(0);
  const missingTopRef = useScrollToListTop(missingPage);
  const [missingRowsPerPage, setMissingRowsPerPage] = useState(50);
  const [issuePage, setIssuePage] = useState(0);
  const issueTopRef = useScrollToListTop(issuePage);
  const [issueRowsPerPage, setIssueRowsPerPage] = useState(50);

  // Uber markup divisor (fetched from system settings)
  const [markupDivisor, setMarkupDivisor] = useState(0.8275);

  useEffect(() => {
    authInstance.get('/admin/tools/uber-markup')
      .then((res) => { if (res.data.success) setMarkupDivisor(res.data.data.divisor); })
      .catch(() => {}); // fallback to default
  }, []);

  // ---------------------------------------------------------------------------
  // Scan
  // ---------------------------------------------------------------------------

  const handleScan = useCallback(async () => {
    setScanning(true);
    setError(null);
    setSuccess(null);
    setResult(null);
    setSelectedSkus(new Set());
    setBatchProgress(null);
    setOrphanPage(0);
    setMissingPage(0);
    setIssuePage(0);
    setIssueTypeFilter('all');

    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/reconcile',
        { storeId: storeId.trim() || undefined },
        { timeout: 120_000 },
      );

      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.error || 'Reconciliation failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Reconciliation failed');
    } finally {
      setScanning(false);
    }
  }, [storeId]);

  // ---------------------------------------------------------------------------
  // Category loading
  // ---------------------------------------------------------------------------

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/categories',
        { storeId: storeId.trim() || undefined },
      );
      if (response.data.success) {
        setCategories(response.data.data?.categories || []);
      }
    } catch {
      // silently fail — categories will be empty
    } finally {
      setLoadingCategories(false);
    }
  }, [storeId]);

  const toggleCategory = (catId: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(catId) ? prev.filter((id) => id !== catId) : [...prev, catId],
    );
  };

  const filteredCategories = categories.filter((c) =>
    c.title.toLowerCase().includes(categorySearch.toLowerCase()),
  );

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------

  const toggleSku = (sku: string) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const toggleAllMissing = () => {
    if (!result) return;
    if (selectedSkus.size === filteredMissing.length) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(filteredMissing.map((p) => p.SKU)));
    }
  };

  // ---------------------------------------------------------------------------
  // Batch add
  // ---------------------------------------------------------------------------

  const handleBatchAdd = async () => {
    if (!result || selectedSkus.size === 0 || selectedCategoryIds.length === 0) return;

    setConfirmBatch(false);
    setBatchAdding(true);
    setError(null);
    setSuccess(null);

    const products = result.inCatalogNotOnUber
      .filter((p) => selectedSkus.has(p.SKU))
      .map((p) => ({
        product: p,
        categoryIds: selectedCategoryIds,
        overrides: {
          price: calcUberPrice(getProductPrice(p), markupDivisor),
        },
      }));

    setBatchProgress({ added: 0, skipped: 0, total: products.length });

    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/add-products-batch',
        {
          storeId: storeId.trim() || undefined,
          products,
        },
        { timeout: 120_000 },
      );

      if (response.data.success) {
        const data = response.data.data;
        setBatchProgress({
          added: data.added?.length || 0,
          skipped: data.skipped?.length || 0,
          total: products.length,
        });
        setSuccess(
          `Batch add complete. ${data.added?.length || 0} added, ${data.skipped?.length || 0} skipped (duplicates).`,
        );
        // Clear selection for added items
        const addedSet = new Set(data.added || []);
        setSelectedSkus((prev) => {
          const next = new Set(prev);
          for (const sku of Array.from(addedSet)) next.delete(sku as string);
          return next;
        });
      } else {
        setError(response.data.error || 'Batch add failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Batch add failed');
    } finally {
      setBatchAdding(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------

  const filteredOrphans = useMemo((): UberOrphan[] => {
    if (!result) return [];
    if (!orphanFilter.trim()) return result.onUberNotInCatalog;
    const q = orphanFilter.toLowerCase();
    return result.onUberNotInCatalog.filter(
      (i) => i.sku.toLowerCase().includes(q) || i.name.toLowerCase().includes(q),
    );
  }, [result, orphanFilter]);

  const filteredMissing = useMemo((): CatalogMissing[] => {
    if (!result) return [];
    if (!missingFilter.trim()) return result.inCatalogNotOnUber;
    const q = missingFilter.toLowerCase();
    return result.inCatalogNotOnUber.filter(
      (p) =>
        p.SKU.toLowerCase().includes(q) ||
        p.Name.toLowerCase().includes(q) ||
        (p.Category || '').toLowerCase().includes(q),
    );
  }, [result, missingFilter]);

  const filteredIssues = useMemo((): ProductIssue[] => {
    if (!result) return [];
    let items = result.issues;
    if (issueTypeFilter === 'price_mismatch') {
      items = items.filter((i) => i.issue !== 'missing_image');
    } else if (issueTypeFilter === 'missing_image') {
      items = items.filter((i) => i.issue !== 'price_mismatch');
    }
    if (!issueFilter.trim()) return items;
    const q = issueFilter.toLowerCase();
    return items.filter(
      (i) =>
        i.sku.toLowerCase().includes(q) ||
        i.name.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q),
    );
  }, [result, issueFilter, issueTypeFilter]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <IconButton onClick={onBack} disabled={scanning || batchAdding}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Uber Menu Reconciliation
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 6 }}>
        Compare your Uber Eats menu against app products. Find orphaned items (on Uber but not in
        DB), missing items (in DB but not on Uber), price mismatches, and missing images.
      </Typography>

      {/* Controls */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              label="Store ID Override (optional)"
              size="small"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              placeholder="Leave blank for default"
              sx={{ flex: '1 1 300px' }}
              disabled={scanning || batchAdding}
              helperText="Token and store ID are auto-configured from AWS"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CartIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleScan}
              disabled={scanning || batchAdding}
              startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <ReconcileIcon />}
              sx={{
                bgcolor: PURPLE,
                '&:hover': { bgcolor: PURPLE_DARK },
                height: 40,
                minWidth: 180,
              }}
            >
              {scanning ? 'Scanning...' : 'Scan & Compare'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Loading */}
      {scanning && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress sx={{ color: PURPLE, mb: 2 }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Fetching Uber menu and catalog products...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This may take 30-60 seconds while paginating through all catalog products.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {result && !scanning && (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {[
              { label: 'Uber Menu Items', value: result.summary.uberItemCount, color: '#1976d2' },
              { label: 'Catalog products', value: result.summary.catalogProductCount, color: '#00897b' },
              {
                label: 'On Uber, not in catalog',
                value: result.summary.onUberNotInCatalog,
                color: result.summary.onUberNotInCatalog > 0 ? '#ed6c02' : '#43a047',
              },
              {
                label: 'In catalog, not on Uber',
                value: result.summary.inCatalogNotOnUber,
                color: result.summary.inCatalogNotOnUber > 0 ? PURPLE : '#43a047',
              },
              {
                label: 'Price Mismatches',
                value: result.summary.priceMismatches,
                color: result.summary.priceMismatches > 0 ? '#d32f2f' : '#43a047',
              },
              {
                label: 'Missing Images',
                value: result.summary.missingImages,
                color: result.summary.missingImages > 0 ? '#ed6c02' : '#43a047',
              },
            ].map((stat) => (
              <Card
                key={stat.label}
                variant="outlined"
                sx={{ flex: '1 1 200px', minWidth: 180 }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: stat.color, mb: 0.5 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              mb: 2,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              '& .Mui-selected': { color: PURPLE },
              '& .MuiTabs-indicator': { bgcolor: PURPLE },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ fontSize: 18, color: '#ed6c02' }} />
                  Orphaned on Uber ({result.summary.onUberNotInCatalog})
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon sx={{ fontSize: 18, color: PURPLE }} />
                  Missing from Uber ({result.summary.inCatalogNotOnUber})
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IssueIcon sx={{ fontSize: 18, color: result.summary.totalIssues > 0 ? '#d32f2f' : '#43a047' }} />
                  Issues ({result.summary.totalIssues})
                </Box>
              }
            />
          </Tabs>

          {/* Tab 0: Orphaned items (on Uber but not in the catalog) */}
          {tab === 0 && (
            <Box>
              {result.onUberNotInCatalog.length === 0 ? (
                <Alert severity="success" icon={<CheckIcon />}>
                  All Uber menu items have matching catalog products. No orphans found.
                </Alert>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    These items exist on the Uber menu but have no matching SKU in the catalog (active +
                    sellable + visible). They may be deprecated, removed, or misconfigured.
                  </Alert>

                  <TextField
                    placeholder="Filter by SKU or name..."
                    size="small"
                    value={orphanFilter}
                    onChange={(e) => { setOrphanFilter(e.target.value); setOrphanPage(0); }}
                    fullWidth
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TableContainer ref={orphanTopRef} component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Uber ID</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }} align="right">Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredOrphans
                          .slice(orphanPage * orphanRowsPerPage, orphanPage * orphanRowsPerPage + orphanRowsPerPage)
                          .map((item) => (
                          <TableRow key={item.sku} hover>
                            <TableCell>
                              <Chip
                                label={item.sku}
                                size="small"
                                variant="outlined"
                                sx={{ fontFamily: 'monospace' }}
                              />
                            </TableCell>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                {item.id}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(item.priceCents / 100)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredOrphans.length}
                    page={orphanPage}
                    onPageChange={(_, p) => setOrphanPage(p)}
                    rowsPerPage={orphanRowsPerPage}
                    onRowsPerPageChange={(e) => { setOrphanRowsPerPage(parseInt(e.target.value, 10)); setOrphanPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                  />
                </>
              )}
            </Box>
          )}

          {/* Tab 2: Issues (price mismatches + missing images) */}
          {tab === 2 && (
            <Box>
              {result.issues.length === 0 ? (
                <Alert severity="success" icon={<CheckIcon />}>
                  All matched products have correct pricing and images. No issues found.
                </Alert>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    These products exist on both Uber and in the app but have pricing or image issues.
                    Prices are compared using markup divisor <strong>{result.summary.markupDivisor}</strong> (Tier1 / {result.summary.markupDivisor}).
                  </Alert>

                  {/* Issue type filter chips */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {([
                      { key: 'all' as const, label: `All Issues (${result.issues.length})` },
                      { key: 'price_mismatch' as const, label: `Price Mismatches (${result.summary.priceMismatches})` },
                      { key: 'missing_image' as const, label: `Missing Images (${result.summary.missingImages})` },
                    ]).map(({ key, label }) => (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        variant={issueTypeFilter === key ? 'filled' : 'outlined'}
                        onClick={() => { setIssueTypeFilter(key); setIssuePage(0); }}
                        icon={key === 'price_mismatch' ? <PriceIcon sx={{ fontSize: 16 }} /> : key === 'missing_image' ? <BrokenImageIcon sx={{ fontSize: 16 }} /> : undefined}
                        sx={{
                          fontWeight: 600,
                          ...(issueTypeFilter === key
                            ? { bgcolor: '#d32f2f', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }
                            : {}),
                        }}
                      />
                    ))}
                  </Box>

                  <TextField
                    placeholder="Filter by SKU, name, or category..."
                    size="small"
                    value={issueFilter}
                    onChange={(e) => { setIssueFilter(e.target.value); setIssuePage(0); }}
                    fullWidth
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TableContainer ref={issueTopRef} component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Issue</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }} align="right">
                            App Price (Tier1)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }} align="right">
                            Uber Price (Actual)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }} align="right">
                            Uber Price (Expected)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Image</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredIssues
                          .slice(issuePage * issueRowsPerPage, issuePage * issueRowsPerPage + issueRowsPerPage)
                          .map((item) => {
                          const hasPriceProblem = item.issue !== 'missing_image';
                          const hasImageProblem = item.issue !== 'price_mismatch';
                          const priceDiff = (item.uberPriceCents - item.expectedPriceCents) / 100;

                          return (
                            <TableRow key={item.sku} hover>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    maxWidth: 220,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.sku}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {item.category || '--'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {hasPriceProblem && (
                                    <Chip
                                      label={`${priceDiff > 0 ? '+' : ''}${formatCurrency(priceDiff)}`}
                                      size="small"
                                      icon={<PriceIcon sx={{ fontSize: 14 }} />}
                                      sx={{
                                        bgcolor: '#ffebee',
                                        color: '#c62828',
                                        fontWeight: 700,
                                        fontFamily: 'monospace',
                                        '& .MuiChip-icon': { color: '#c62828' },
                                      }}
                                    />
                                  )}
                                  {hasImageProblem && (
                                    <Chip
                                      label="No image"
                                      size="small"
                                      icon={<BrokenImageIcon sx={{ fontSize: 14 }} />}
                                      sx={{
                                        bgcolor: '#fff3e0',
                                        color: '#e65100',
                                        fontWeight: 600,
                                        '& .MuiChip-icon': { color: '#e65100' },
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {formatCurrency(item.dbPriceTier1)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    color: hasPriceProblem ? '#c62828' : 'text.primary',
                                  }}
                                >
                                  {formatCurrency(item.uberPriceCents / 100)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    color: hasPriceProblem ? '#2e7d32' : 'text.primary',
                                  }}
                                >
                                  {formatCurrency(item.expectedPriceCents / 100)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {item.imageUrl ? (
                                  <Avatar
                                    variant="rounded"
                                    src={item.imageUrl}
                                    sx={{ width: 32, height: 32, borderRadius: 1 }}
                                  />
                                ) : (
                                  <BrokenImageIcon sx={{ fontSize: 24, color: '#e65100' }} />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredIssues.length}
                    page={issuePage}
                    onPageChange={(_, p) => setIssuePage(p)}
                    rowsPerPage={issueRowsPerPage}
                    onRowsPerPageChange={(e) => { setIssueRowsPerPage(parseInt(e.target.value, 10)); setIssuePage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                  />
                </>
              )}
            </Box>
          )}

          {/* Tab 1: Missing from Uber (in the catalog but not on Uber) */}
          {tab === 1 && (
            <Box>
              {result.inCatalogNotOnUber.length === 0 ? (
                <Alert severity="success" icon={<CheckIcon />}>
                  All sellable catalog products are on the Uber menu. Nothing missing.
                </Alert>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    These catalog products (active, sellable, visible) are not on the Uber menu.
                    Select items and use batch-add to publish them all at once.
                  </Alert>

                  {/* Batch add controls */}
                  <Card variant="outlined" sx={{ mb: 2, bgcolor: PURPLE_LIGHT }}>
                    <CardContent sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE }}>
                          Batch Add: {selectedSkus.size} selected
                        </Typography>

                        <Chip
                          label={`Price: Tier1 / ${markupDivisor}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', borderColor: PURPLE, color: PURPLE }}
                        />

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (categories.length === 0) loadCategories();
                          }}
                          disabled={loadingCategories || categories.length > 0}
                          sx={{ borderColor: PURPLE, color: PURPLE }}
                        >
                          {loadingCategories
                            ? 'Loading...'
                            : categories.length > 0
                              ? `${selectedCategoryIds.length} categories`
                              : 'Load Categories'}
                        </Button>

                        <Box sx={{ flex: 1 }} />

                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setConfirmBatch(true)}
                          disabled={
                            selectedSkus.size === 0 ||
                            selectedCategoryIds.length === 0 ||
                            batchAdding
                          }
                          startIcon={
                            batchAdding ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <AddIcon />
                            )
                          }
                          sx={{
                            bgcolor: PURPLE,
                            '&:hover': { bgcolor: PURPLE_DARK },
                          }}
                        >
                          {batchAdding
                            ? 'Adding...'
                            : selectedCategoryIds.length === 0
                              ? 'Select categories first'
                              : `Add ${selectedSkus.size} to Uber`}
                        </Button>
                      </Box>

                      {/* Category selector (shown after loading) */}
                      {categories.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            placeholder="Filter categories..."
                            size="small"
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            fullWidth
                            sx={{ mb: 1.5 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {filteredCategories.map((cat) => {
                              const isSelected = selectedCategoryIds.includes(cat.id);
                              return (
                                <Chip
                                  key={cat.id}
                                  label={`${cat.title} (${cat.itemCount})`}
                                  size="small"
                                  variant={isSelected ? 'filled' : 'outlined'}
                                  onClick={() => toggleCategory(cat.id)}
                                  sx={{
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    ...(isSelected
                                      ? { bgcolor: PURPLE, color: '#fff', '&:hover': { bgcolor: PURPLE_DARK } }
                                      : { '&:hover': { borderColor: PURPLE, bgcolor: PURPLE_LIGHT } }),
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {batchProgress && (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress
                            variant="determinate"
                            value={
                              batchProgress.total > 0
                                ? ((batchProgress.added + batchProgress.skipped) / batchProgress.total) * 100
                                : 0
                            }
                            sx={{
                              borderRadius: 2,
                              '& .MuiLinearProgress-bar': { bgcolor: PURPLE },
                              bgcolor: '#e0e0e0',
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {batchProgress.added} added, {batchProgress.skipped} skipped of{' '}
                            {batchProgress.total}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Filter */}
                  <TextField
                    placeholder="Filter by SKU, name, or category..."
                    size="small"
                    value={missingFilter}
                    onChange={(e) => { setMissingFilter(e.target.value); setMissingPage(0); }}
                    fullWidth
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Table */}
                  <TableContainer ref={missingTopRef} component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ bgcolor: '#fafafa' }}>
                            <Checkbox
                              indeterminate={
                                selectedSkus.size > 0 &&
                                selectedSkus.size < filteredMissing.length
                              }
                              checked={
                                filteredMissing.length > 0 &&
                                selectedSkus.size === filteredMissing.length
                              }
                              onChange={toggleAllMissing}
                              sx={{ '&.Mui-checked': { color: PURPLE } }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }} align="right">
                            Catalog price
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }} align="right">
                            Uber Price (/ {markupDivisor})
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredMissing
                          .slice(missingPage * missingRowsPerPage, missingPage * missingRowsPerPage + missingRowsPerPage)
                          .map((product) => {
                          const isSelected = selectedSkus.has(product.SKU);
                          const catalogPrice = getProductPrice(product);
                          const uberPrice = calcUberPrice(catalogPrice, markupDivisor);

                          return (
                            <TableRow
                              key={product.SKU}
                              hover
                              selected={isSelected}
                              onClick={() => toggleSku(product.SKU)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isSelected}
                                  sx={{ '&.Mui-checked': { color: PURPLE } }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar
                                    variant="rounded"
                                    src={getProductImage(product) || undefined}
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: 1,
                                      bgcolor: '#e0e0e0',
                                      fontSize: 14,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {product.Name?.charAt(0)?.toUpperCase() || '?'}
                                  </Avatar>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      maxWidth: 280,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {product.Name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={product.SKU}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {product.Category || '--'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {formatCurrency(catalogPrice)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', fontWeight: 700, color: PURPLE }}
                                >
                                  {formatCurrency(uberPrice)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredMissing.length}
                    page={missingPage}
                    onPageChange={(_, p) => setMissingPage(p)}
                    rowsPerPage={missingRowsPerPage}
                    onRowsPerPageChange={(e) => { setMissingRowsPerPage(parseInt(e.target.value, 10)); setMissingPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                  />
                </>
              )}
            </Box>
          )}
        </>
      )}

      {/* Empty state */}
      {!scanning && !result && !error && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <ReconcileIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Click &quot;Scan &amp; Compare&quot; to start
            </Typography>
            <Typography variant="body2" color="text.disabled">
              This will download the full Uber menu and all catalog products, then compare SKUs.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Batch confirm dialog */}
      <Dialog
        open={confirmBatch}
        onClose={() => !batchAdding && setConfirmBatch(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Batch Add to Uber</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will add <strong>{selectedSkus.size} products</strong> to the live Uber Eats menu.
            A backup will be created automatically before any changes.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Products:</strong> {selectedSkus.size} selected
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Pricing:</strong> Tier 1 / {markupDivisor} (Uber commission formula)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Categories:</strong>{' '}
            {categories
              .filter((c) => selectedCategoryIds.includes(c.id))
              .map((c) => c.title)
              .join(', ') || 'None selected'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmBatch(false)} disabled={batchAdding}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBatchAdd}
            disabled={batchAdding}
            startIcon={batchAdding ? <CircularProgress size={18} /> : <AddIcon />}
            sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: PURPLE_DARK } }}
          >
            {batchAdding ? 'Adding...' : 'Confirm & Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UberMenuReconciliation;
